import queryString from 'query-string';
import Prismic from 'prismic-javascript';

export const ContentTypes = {
  CarriersInformation: 'carriers_information',
  // Shared partner co-branding document, also consumed by quote-fe's getPartnerAssets.
  // Adding a partner is a content change in this Prismic doc, never a code change.
  PartnerAssets: 'quote-partner-data'
};

// Mapped query operators
const validOperators = {
  and: 'at',
  or: 'any',
  '>': 'gt',
  '<': 'lt',
  between: 'inRange',
  in: 'in',
  not: 'not'
};

const mapOptionsToQuery = (type, queryOptions) =>
  Object.entries(queryOptions).reduce((res, [key, { value, isCustom = false, operator = 'and' }]) => {
    const mappedOperator = validOperators[operator];
    const path = isCustom ? `my.${type}.${key}` : `document.${key}`;
    if (!mappedOperator) {
      return res;
    }
    return [...res, Prismic.Predicates[mappedOperator](path, value)];
  }, []);

const apiEndpoint = 'https://branch.prismic.io/api/v2';
let _masterRef = null;

const getMasterRef = () => {
  if (_masterRef) {
    return Promise.resolve(_masterRef);
  }
  return fetch(apiEndpoint)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Prismic API request failed with status ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then((data) => data.refs.find((ref) => ref.isMasterRef))
    .then((ref) => {
      _masterRef = ref;
      return _masterRef;
    });
};

export const getSingle = async (documentType, fetchLinks = null, ref = null) => {
  const searchByType = async () => {
    const masterRef = await getMasterRef();
    const query = queryString.stringify({
      page: 1,
      pageSize: 1,
      fetchLinks,
      ref: ref || masterRef.ref,
      q: `[[at(document.type, "${documentType}")]]`
    });

    return fetch(`${apiEndpoint}/documents/search?${query}`);
  };

  let response = await searchByType();
  if ((response.status === 410 || response.status === 404) && !ref) {
    // A stale cached master ref (expired by a Prismic publish) returns 410 Gone, decaying to 404
    // once it ages out entirely. Invalidate and retry once with a fresh ref; a repeat failure
    // falls through and throws. Caller-supplied refs can't be fixed by a retry.
    _masterRef = null;
    response = await searchByType();
  }
  if (!response.ok) {
    throw new Error(`Prismic API request failed with status ${response.status}: ${response.statusText}`);
  }
  const data = await response.json();
  if (data && data.results && data.results.length) {
    return data.results[0];
  }
};

export const Client = (req = null) => Prismic.client(apiEndpoint, { req });

export const getRepeatable = async (documentType, uid, req, ref, otherOptions = {}) => {
  const content = await Client(req).getByUID(documentType, uid, { ...otherOptions, ref });
  if (!content || !content.data) {
    throw new Error(`Content not found for Document type: ${documentType}, uid: ${uid}`);
  }
  return content;
};

export const getByQuery = (documentType, req, queryOptions, ref, otherOptions = {}) => {
  const queryParams = [
    ...mapOptionsToQuery(documentType, queryOptions),
    Prismic.Predicates.at('document.type', documentType)
  ];

  return Client(req).query(queryParams, { ...otherOptions, ref });
};
